import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/auth';
import { getApiBaseUrl } from '../utils/api';
import { toast } from 'react-hot-toast';

const AnnouncementManagement: React.FC = () => {
    const [message, setMessage] = useState('');
    const [currentAnnouncement, setCurrentAnnouncement] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCurrentAnnouncement();
    }, []);

    const fetchCurrentAnnouncement = async () => {
        try {
            const res = await fetch(`${getApiBaseUrl()}/announcements/active`);
            const data = await res.json();
            if (data.announcement) {
                setCurrentAnnouncement(data.announcement);
                setMessage(data.announcement.message);
            }
        } catch (error) {
            console.error('Failed to fetch announcement:', error);
        }
    };

    const handleCreate = async () => {
        if (!message.trim()) {
            toast.error('Vui lòng nhập nội dung thông báo');
            return;
        }

        if (message.length < 5) {
            toast.error('Thông báo phải có ít nhất 5 ký tự');
            return;
        }

        if (message.length > 500) {
            toast.error('Thông báo không được quá 500 ký tự');
            return;
        }

        setLoading(true);
        try {
            const token = getToken();
            const res = await fetch(`${getApiBaseUrl()}/announcements`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ message: message.trim() })
            });

            if (!res.ok) {
                throw new Error('Failed to create announcement');
            }

            toast.success('Đã tạo thông báo mới!');
            await fetchCurrentAnnouncement();
        } catch (error) {
            console.error('Failed to create announcement:', error);
            toast.error('Không thể tạo thông báo');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentAnnouncement) return;

        if (!window.confirm('Bạn có chắc muốn xóa thông báo này?')) {
            return;
        }

        setLoading(true);
        try {
            const token = getToken();
            const res = await fetch(`${getApiBaseUrl()}/announcements?id=${currentAnnouncement.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) {
                throw new Error('Failed to delete announcement');
            }

            toast.success('Đã xóa thông báo!');
            setCurrentAnnouncement(null);
            setMessage('');
        } catch (error) {
            console.error('Failed to delete announcement:', error);
            toast.error('Không thể xóa thông báo');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="card p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Quản lý Thông Báo
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Tạo thông báo hiển thị dạng banner chạy quanh trang web
                </p>

                {/* Current Announcement */}
                {currentAnnouncement && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                    Thông báo hiện tại:
                                </h3>
                                <p className="text-gray-700 dark:text-gray-300">
                                    {currentAnnouncement.message}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                    Tạo lúc: {new Date(currentAnnouncement.createdAt).toLocaleString('vi-VN')}
                                </p>
                            </div>
                            <button
                                onClick={handleDelete}
                                disabled={loading}
                                className="btn-danger text-sm px-4 py-2"
                            >
                                Xóa
                            </button>
                        </div>
                    </div>
                )}

                {/* Create Form */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nội dung thông báo
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Nhập nội dung thông báo (5-500 ký tự)..."
                            className="w-full h-24 resize-none px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 
                            bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 
                            text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                            maxLength={500}
                        />
                        <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {message.length}/500 ký tự
                            </p>
                            {message.length < 5 && message.length > 0 && (
                                <p className="text-xs text-red-500">
                                    Tối thiểu 5 ký tự
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleCreate}
                            disabled={loading || message.length < 5}
                            className="btn-primary px-6 py-2"
                        >
                            {loading ? 'Đang xử lý...' : currentAnnouncement ? 'Cập nhật thông báo' : 'Tạo thông báo'}
                        </button>
                        {message !== (currentAnnouncement?.message || '') && (
                            <button
                                onClick={() => setMessage(currentAnnouncement?.message || '')}
                                className="btn-secondary px-6 py-2"
                            >
                                Hủy
                            </button>
                        )}
                    </div>
                </div>

                {/* Preview */}
                {message.trim() && (
                    <div className="mt-6">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Xem trước:
                        </h3>
                        <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 dark:from-blue-900 dark:via-blue-800 dark:to-blue-900 text-white py-2.5 px-4 rounded-lg overflow-hidden border border-blue-400 dark:border-blue-700 shadow-md">
                            <div className="flex items-center gap-4">
                                <svg className="w-5 h-5 flex-shrink-0 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                                <div className="flex-1 overflow-hidden">
                                    <div className="whitespace-nowrap animate-marquee font-semibold">
                                        {message}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Instructions */}
            <div className="card p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">
                    📌 Hướng dẫn sử dụng
                </h3>
                <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                    <li>• Thông báo sẽ hiển thị ở đầu trang web dạng banner chạy quanh</li>
                    <li>• Chỉ có thể có 1 thông báo hoạt động tại một thời điểm</li>
                    <li>• Tạo thông báo mới sẽ tự động thay thế thông báo cũ</li>
                    <li>• Người dùng có thể đóng thông báo bằng nút X</li>
                    <li>• Thông báo tự động cập nhật mỗi 60 giây</li>
                </ul>
            </div>
        </div>
    );
};

export default AnnouncementManagement;
